import { Helmet } from "react-helmet-async";
import { websiteSchema, publisherOrgSchema, PERSON_ID, PUBLISHER_ID } from "@/lib/seo";

type JsonLd = Record<string, any>;

/**
 * X / Twitter handle for twitter:site and twitter:creator. Left empty on
 * purpose: Dr. Refai doesn't hold a claimed X account, and the network
 * standard forbids inventing one. Set it (e.g. "@handle") once an account
 * exists and both tags will start rendering on every page.
 */
const TWITTER_HANDLE = "";

function collect(node: unknown, fn: (n: JsonLd) => void): void {
  if (Array.isArray(node)) return node.forEach((n) => collect(n, fn));
  if (!node || typeof node !== "object") return;
  fn(node as JsonLd);
  Object.values(node as JsonLd).forEach((v) => collect(v, fn));
}

function hasType(n: JsonLd, t: string): boolean {
  const v = n["@type"];
  return Array.isArray(v) ? v.includes(t) : v === t;
}

/**
 * Network standard section 3: every page carries WebSite, Organization, a
 * Person reference, and exactly one BreadcrumbList.
 *
 * Page callers historically passed a BreadcrumbList both nested inside the
 * WebPage node (webPageSchema's `breadcrumb`) and as a top-level block, so
 * pages emitted two. Here the top-level list wins, gets a stable @id, and the
 * nested copy is replaced with an @id reference. If a page only has the
 * nested copy, it's left alone (still exactly one).
 */
function normalizeSchemas(input: JsonLd[], canonical: string): JsonLd[] {
  const out = input.map((s) => ({ ...s }));
  const topLevel = out.filter((s) => hasType(s, "BreadcrumbList"));
  if (topLevel.length > 0) {
    const keep = topLevel[0];
    keep["@id"] = keep["@id"] ?? `${canonical}#breadcrumb`;
    for (let i = out.length - 1; i >= 0; i--) {
      if (hasType(out[i], "BreadcrumbList") && out[i] !== keep) out.splice(i, 1);
    }
    for (const s of out) {
      if (s.breadcrumb && typeof s.breadcrumb === "object" && hasType(s.breadcrumb, "BreadcrumbList")) {
        s.breadcrumb = { "@id": keep["@id"] };
      }
    }
  }

  let hasWebsite = false;
  let hasOrg = false;
  let hasPerson = false;
  collect(out, (n) => {
    if (hasType(n, "WebSite")) hasWebsite = true;
    if (n["@id"] === PUBLISHER_ID && n["@type"]) hasOrg = true;
    if (n["@id"] === PERSON_ID) hasPerson = true;
  });
  if (!hasWebsite) out.push(websiteSchema);
  if (!hasOrg) out.push(publisherOrgSchema);
  if (!hasPerson) {
    out.push({
      "@context": "https://schema.org",
      "@type": "Person",
      "@id": PERSON_ID,
      name: "Shariq Refai",
      url: "https://shariqrefai.com/",
    });
  }
  return out;
}

interface SEOProps {
  title: string;
  description: string;
  url?: string;
  image?: string;
  type?: string;
  noindex?: boolean;
  /** Optional shorter, more shareable variant used for og:title only. */
  ogTitle?: string;
  /** Optional shareable variant used for og:description only. */
  ogDescription?: string;
  /** Optional shorter variant used for twitter:title only. */
  twitterTitle?: string;
  /** Optional shareable variant used for twitter:description only. */
  twitterDescription?: string;
  /**
   * @deprecated Search engines and AI answer engines ignore the keywords meta.
   * Kept for backward compatibility with existing page callers but no longer
   * emitted. Remove callers over time.
   */
  keywords?: string;
  schema?: JsonLd | JsonLd[];
  articlePublishedTime?: string;
  articleModifiedTime?: string;
  articleSection?: string;
  articleTags?: string[];
  articleAuthor?: string;
}

const SITE_NAME = "Shariq Refai, MD, MBA";
const SITE_ORIGIN = "https://shariqrefai.com";
const DEFAULT_IMAGE = `${SITE_ORIGIN}/opengraph.jpg`;

/**
 * Per-page social card generated by scripts/generate-og.mjs:
 * "/" -> /og/home.jpg, "/a/b" -> /og/a--b.jpg.
 */
function generatedOgImage(url: string): string {
  const path = url.replace(/^https?:\/\/[^/]+/, "").split(/[?#]/)[0] || "/";
  const slug = path === "/" ? "home" : path.replace(/^\/+|\/+$/g, "").replace(/\//g, "--");
  return `${SITE_ORIGIN}/og/${slug}.jpg`;
}

export function SEO({
  title,
  description,
  url = SITE_ORIGIN,
  image,
  type = "website",
  noindex = false,
  keywords: _keywords,
  ogTitle,
  ogDescription,
  twitterTitle,
  twitterDescription,
  schema,
  articlePublishedTime,
  articleModifiedTime,
  articleSection,
  articleTags,
  articleAuthor = `${SITE_ORIGIN}/about`,
}: SEOProps) {
  // Build the canonical URL. Cloudflare Pages serves the prerendered directory
  // index at /route/ (with trailing slash), so canonicals must include the slash
  // to match what is actually served. Root ("/") is left alone.
  const canonicalPath = (() => {
    // Normalize to a full URL first.
    let full;
    if (url.startsWith("http")) {
      full = url;
    } else {
      const p = url.startsWith("/") ? url : `/${url}`;
      full = `${SITE_ORIGIN}${p}`;
    }
    // Cloudflare Pages serves each prerendered route from a /route/ directory URL,
    // so canonicals must include the trailing slash to match what is actually
    // served. Skip normalization for the root and for URLs whose path already
    // ends with a slash. Preserve any query or hash by inserting the slash
    // before them.
    const m = full.match(/^(https?:\/\/[^/]+)(\/[^?#]*)([?#].*)?$/);
    if (!m) return full;
    const [, origin, path, tail] = m;
    if (path === "/" || path.endsWith("/")) return full;
    return `${origin}${path}/${tail ?? ""}`;
  })();
  const canonical = canonicalPath;
  if (!image) image = noindex ? DEFAULT_IMAGE : generatedOgImage(url);
  const ogImage = image.startsWith("http") ? image : `${SITE_ORIGIN}${image.startsWith("/") ? image : `/${image}`}`;
  const rawSchemas: JsonLd[] = Array.isArray(schema) ? schema : schema ? [schema] : [];
  const schemas = noindex ? rawSchemas : normalizeSchemas(rawSchemas, canonical);

  const effectiveOgTitle = ogTitle ?? title;
  const effectiveOgDescription = ogDescription ?? description;
  const effectiveTwitterTitle = twitterTitle ?? ogTitle ?? title;
  const effectiveTwitterDescription = twitterDescription ?? ogDescription ?? description;

  return (
    <Helmet htmlAttributes={{ lang: "en" }}>
      <title>{title}</title>
      {noindex ? null : <link rel="canonical" href={canonical} />}
      <link rel="sitemap" type="application/xml" href="/sitemap-index.xml" />
      <link
        rel="alternate"
        type="application/rss+xml"
        title="Perspectives by Shariq Refai, MD, MBA"
        href="https://shariqrefai.com/rss.xml"
      />
      <link
        rel="alternate"
        type="application/feed+json"
        title="Perspectives by Shariq Refai, MD, MBA"
        href="https://shariqrefai.com/feed.json"
      />
      <meta name="description" content={description} />
      <meta
        name="robots"
        content={noindex ? "noindex,nofollow" : "index,follow,max-image-preview:large,max-snippet:-1,max-video-preview:-1"}
      />
      <meta name="author" content="Shariq Refai, MD, MBA, FAPA" />

      {/* Open Graph */}
      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:locale" content="en_US" />
      <meta property="og:title" content={effectiveOgTitle} />
      <meta property="og:description" content={effectiveOgDescription} />
      <meta property="og:image" content={ogImage} />
      <meta property="og:image:type" content={ogImage.endsWith(".png") ? "image/png" : ogImage.endsWith(".webp") ? "image/webp" : "image/jpeg"} />
      <meta property="og:image:width" content="1200" />
      <meta property="og:image:height" content="630" />
      <meta property="og:image:alt" content={effectiveOgTitle} />
      <meta property="og:url" content={canonical} />
      <meta property="og:type" content={type} />

      {type === "article" && articlePublishedTime ? (
        <meta property="article:published_time" content={articlePublishedTime} />
      ) : null}
      {type === "article" && articleModifiedTime ? (
        <meta property="article:modified_time" content={articleModifiedTime} />
      ) : null}
      {type === "article" ? (
        <meta property="article:author" content={articleAuthor} />
      ) : null}
      {type === "article" && articleSection ? (
        <meta property="article:section" content={articleSection} />
      ) : null}
      {type === "article" && articleTags
        ? articleTags.map((tag) => (
            <meta key={tag} property="article:tag" content={tag} />
          ))
        : null}

      {/* Twitter / X. twitter:site and twitter:creator render only once
          TWITTER_HANDLE is set (see the note at the top of this file). */}
      <meta name="twitter:card" content="summary_large_image" />
      {TWITTER_HANDLE ? <meta name="twitter:site" content={TWITTER_HANDLE} /> : null}
      {TWITTER_HANDLE ? <meta name="twitter:creator" content={TWITTER_HANDLE} /> : null}
      <meta name="twitter:title" content={effectiveTwitterTitle} />
      <meta name="twitter:description" content={effectiveTwitterDescription} />
      <meta name="twitter:image" content={ogImage} />
      <meta name="twitter:image:alt" content={effectiveTwitterTitle} />

      {schemas.map((s, i) => (
        <script key={i} type="application/ld+json">
          {JSON.stringify(s)}
        </script>
      ))}
    </Helmet>
  );
}
